/**
 * CityModule — the Cities tab (P14.2).
 *
 * Surfaces the SWG player-city layer: found a city on a deployed City Hall, see
 * your city's rank / radius / treasury / citizens, and (as mayor) set taxes, adopt
 * a specialization, and run a mayoral election. The client RENDERS replicated
 * rows + CALLS reducers; every rank, tax, treasury, and election decision is
 * server-authoritative. The 1000m placement map + civic zoning UI are
 * world-canvas features; this panel wires the authoritative governance actions.
 */

import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { CITY_RANK_NAMES, CITY_SPECIALIZATIONS, CITY_MAX_TAX_PCT } from '@shared/types/cities.js';
import { iconMarkup } from '../lib/icons.js';

export class CityModule implements IModule {
  readonly name = 'Cities';
  private bound = false;
  private els: Record<string, HTMLElement> = {};

  init(_app: App): void {
    for (const id of ['ct-found', 'ct-status', 'ct-mayor', 'ct-election', 'ct-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => {
      this.bind();
      this.render();
    });
    for (const ev of ['atlas:cities-updated', 'atlas:structures-updated'] as const) {
      bus.on(ev, () => this.render());
    }
    this.render();
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    const ping = (): void => bus.emit('atlas:cities-updated', {});
    for (const tbl of [conn.db.city, conn.db.citizen, conn.db.civicStructure, conn.db.mayorElection, conn.db.mayorVote]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
  }

  private myCity(conn: DbConnection, me: Identity) {
    for (const c of conn.db.citizen.iter()) {
      if (c.identity.isEqual(me)) return conn.db.city.cityId.find(c.cityId) ?? null;
    }
    return null;
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    const me = conn.identity;
    const city = this.myCity(conn, me);
    this.renderFound(conn, me, city);
    this.renderStatus(conn, me, city);
    this.renderMayor(conn, me, city);
    this.renderElection(conn, me, city);
  }

  private renderFound(conn: DbConnection, me: Identity, city: ReturnType<CityModule['myCity']>): void {
    const el = this.els['ct-found'];
    if (!el) return;
    if (city) {
      el.innerHTML = '';
      return;
    }
    // Need a deployed City Hall the player owns that doesn't yet anchor a city.
    const hall = [...conn.db.structure.iter()].find(
      (s) => s.owner.isEqual(me) && s.structureType === 'city_hall' && !conn.db.civicStructure.structureId.find(s.structureId),
    );
    el.innerHTML = hall
      ? `<div class="meta">You have a City Hall (#${hall.structureId}) ready to anchor a city.</div>` +
        `<div class="row"><input id="ct-name" placeholder="City name" maxlength="48" /><button class="action" id="ct-found-btn">Found city</button></div>`
      : '<div class="empty">Deploy a City Hall (Structures tab) onto the Commons, then found a city on it.</div>';
    el.querySelector('#ct-found-btn')?.addEventListener('click', () => {
      const name = (el.querySelector('#ct-name') as HTMLInputElement | null)?.value || 'New City';
      this.found(hall!.structureId, name);
    });
  }

  private renderStatus(conn: DbConnection, _me: Identity, city: ReturnType<CityModule['myCity']>): void {
    const el = this.els['ct-status'];
    if (!el) return;
    if (!city) {
      el.innerHTML = '<div class="empty">You are not a citizen of a city.</div>';
      return;
    }
    const citizens = [...conn.db.citizen.iter()].filter((c) => c.cityId === city.cityId).length;
    const civics = [...conn.db.civicStructure.iter()].filter((c) => c.cityId === city.cityId);
    const rankName = CITY_RANK_NAMES[city.cityRank - 1] ?? 'invalid';
    el.innerHTML =
      `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('bank')}${this.esc(city.name)}</span><span class="price">${this.esc(rankName)} (rank ${city.cityRank})</span></div>` +
      `<div class="meta">status ${this.esc(city.status)} · citizens ${citizens} · radius ${city.radiusM}m · treasury ${city.treasury} SOV</div>` +
      `<div class="meta">taxes — income ${city.incomeTax}% · property ${city.propertyTax}% · sales ${city.salesTax}%${city.specialization ? ` · specialization ${this.esc(city.specialization)}` : ''}</div></div>` +
      (civics.length
        ? `<div class="meta">civic structures: ${civics.map((c) => `${this.esc(c.civicType.replace(/_/g, ' '))} (${this.esc(c.status)})`).join(', ')}</div>`
        : '');
  }

  private renderMayor(conn: DbConnection, me: Identity, city: ReturnType<CityModule['myCity']>): void {
    const el = this.els['ct-mayor'];
    if (!el) return;
    if (!city || !city.mayor.isEqual(me)) {
      el.innerHTML = '';
      return;
    }
    const specOpts = ['', ...CITY_SPECIALIZATIONS]
      .map((s) => `<option value="${s}"${s === city.specialization ? ' selected' : ''}>${s || '(none)'}</option>`)
      .join('');
    el.innerHTML =
      `<div class="meta">${iconMarkup('check')}You are the mayor.</div>` +
      `<div class="row"><label>Property %</label><input id="ct-prop" type="number" min="0" max="${CITY_MAX_TAX_PCT}" value="${city.propertyTax}" style="width:60px" />` +
      `<label>Sales %</label><input id="ct-sales" type="number" min="0" max="${CITY_MAX_TAX_PCT}" value="${city.salesTax}" style="width:60px" />` +
      `<button class="action secondary" id="ct-tax-btn">Set taxes</button></div>` +
      (city.cityRank >= 3
        ? `<div class="row"><label>Specialization</label><select id="ct-spec">${specOpts}</select><button class="action secondary" id="ct-spec-btn">Adopt</button></div>`
        : `<div class="meta">Reach rank 3 (Township) to adopt a specialization.</div>`) +
      `<button class="action" id="ct-election-btn">Open mayoral election</button>`;
    el.querySelector('#ct-tax-btn')?.addEventListener('click', () => {
      const prop = Number((el.querySelector('#ct-prop') as HTMLInputElement).value) || 0;
      const sales = Number((el.querySelector('#ct-sales') as HTMLInputElement).value) || 0;
      this.setTax(city.cityId, prop, sales);
    });
    el.querySelector('#ct-spec-btn')?.addEventListener('click', () => {
      const spec = (el.querySelector('#ct-spec') as HTMLSelectElement).value;
      this.setSpec(city.cityId, spec);
    });
    el.querySelector('#ct-election-btn')?.addEventListener('click', () => this.openElection(city.cityId));
  }

  private renderElection(conn: DbConnection, me: Identity, city: ReturnType<CityModule['myCity']>): void {
    const el = this.els['ct-election'];
    if (!el) return;
    if (!city) {
      el.innerHTML = '';
      return;
    }
    const open = [...conn.db.mayorElection.iter()].find((e) => e.cityId === city.cityId && !e.resolved);
    if (!open) {
      el.innerHTML = '';
      return;
    }
    el.innerHTML =
      `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('check')}Mayoral election #${open.electionId}</span></div>` +
      `<div class="meta">Vote for yourself, or close the election when the window ends.</div>` +
      `<button class="action" id="ct-vote-btn">Vote for me</button> <button class="action secondary" id="ct-close-btn">Close election</button></div>`;
    el.querySelector('#ct-vote-btn')?.addEventListener('click', () => this.vote(open.electionId, me));
    el.querySelector('#ct-close-btn')?.addEventListener('click', () => this.closeElection(open.electionId));
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  private found(cityHallStructureId: bigint, name: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .foundCity({ cityHallStructureId, name })
      .then(() => this.note('City founded.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private setTax(cityId: bigint, property: number, sales: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .setCityTax({ cityId, income: 0, property, sales })
      .then(() => this.note('Taxes updated.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private setSpec(cityId: bigint, spec: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .setSpecialization({ cityId, spec })
      .then(() => this.note(spec ? `Adopted ${spec}.` : 'Specialization cleared.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private openElection(cityId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .openMayorElection({ cityId })
      .then(() => this.note('Election opened.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private vote(electionId: bigint, candidate: Identity): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .voteMayor({ electionId, candidate })
      .then(() => this.note('Vote recorded.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private closeElection(electionId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .closeMayorElection({ electionId })
      .then(() => this.note('Election closed.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private note(text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els['ct-note'];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));
  }
}
